import { useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import { Th } from '../../utils/api';
import { LineChart, StackedBar, ChartCard, ChartLoader, ChartError } from '../../components/Charts';
import { MultiFilterSelect } from '../../components/MultiFilterSelect';
import {
  APP_COLORS, ALPHA_COLORS, ALPHA_LABELS, appColor,
  FilterSelect, useThFilters,
} from './thShared';
import ThWeeklyGeoView from './ThWeeklyGeoView';

const CHART_H = 300;

export default function ThWeeklyView({ vertical = 'food', subView = 'charts' }) {
  const {
    filters, filtersLoaded,
    appNames, setAppNames,
    alphas, setAlphas,
    province, handleProvinceChange,
    district, handleDistrictChange,
    category, setCategory,
    availableDistricts,
  } = useThFilters(vertical);

  const params = useMemo(() => ({
    vertical,
    app_name: appNames,
    alpha:    alphas,
    province,
    district,
    category,
  }), [vertical, appNames, alphas, province, district, category]);

  const { data, loading, error } = useApi(
    () => Th.weekly(vertical, {
      app_name: appNames, alpha: alphas, province, district, category,
    }),
    params,
    filtersLoaded,
  );

  const weeks          = useMemo(() => [...new Set((data || []).map(r => r.week))].sort().slice(-6), [data]);
  const appNamesInData = useMemo(() => [...new Set((data || []).map(r => r.app_name))].sort(), [data]);

  function buildSeries(valueKey) {
    return appNamesInData.map((app, i) => {
      const byWeek = Object.fromEntries(
        (data || []).filter(r => r.app_name === app).map(r => [r.week, r[valueKey]])
      );
      return { name: app, color: appColor(app, i), data: weeks.map(w => byWeek[w] ?? null) };
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userSeries    = useMemo(() => buildSeries('unique_visitors'), [data, appNamesInData, weeks]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sessionSeries = useMemo(() => buildSeries('total_sessions'),  [data, appNamesInData, weeks]);

  const empty = <div className="empty-state">No data for selected filters</div>;

  if (subView === 'geography') {
    return <ThWeeklyGeoView vertical={vertical} />;
  }

  return (
    <div>
      <div className="filters-bar" style={{ alignItems: 'flex-start' }}>
        <FilterSelect label="Category" value={category} onChange={setCategory} options={filters.categories} />
        <MultiFilterSelect
          label="App"
          values={appNames}
          onChange={setAppNames}
          options={filters.app_names}
          chipColor={v => APP_COLORS[v] || '#6366f1'}
        />
        <MultiFilterSelect
          label="Alpha"
          values={alphas}
          onChange={setAlphas}
          options={filters.alphas}
          chipColor={v => ALPHA_COLORS[v] || '#6366f1'}
          labelMap={ALPHA_LABELS}
        />
        <FilterSelect label="Province" value={province} onChange={handleProvinceChange} options={filters.provinces} />
        <FilterSelect label="District" value={district} onChange={handleDistrictChange} options={availableDistricts} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Row 1 — Market share stacked bars */}
        <ChartCard title="Market Share — Unique Visitors" sub={`${category} · weekly % share by app`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (userSeries.length > 0
            ? <StackedBar categories={weeks} series={userSeries} height={CHART_H} normalize />
            : empty)}
        </ChartCard>

        <ChartCard title="Market Share — Sessions" sub={`${category} · weekly % share by app`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (sessionSeries.length > 0
            ? <StackedBar categories={weeks} series={sessionSeries} height={CHART_H} normalize />
            : empty)}
        </ChartCard>

        {/* Row 2 — Absolute line charts */}
        <ChartCard title="Weekly Unique Visitors — by App" sub={`${category} · last 6 weeks`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (userSeries.length > 0
            ? <LineChart categories={weeks} series={userSeries} height={CHART_H} />
            : empty)}
        </ChartCard>

        <ChartCard title="Weekly Sessions — by App" sub={`${category} · last 6 weeks`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (sessionSeries.length > 0
            ? <LineChart categories={weeks} series={sessionSeries} height={CHART_H} />
            : empty)}
        </ChartCard>

      </div>
    </div>
  );
}
